import React from "react";
import { motion } from "framer-motion";
import { Trophy, CheckCircle, Star } from "lucide-react";
import AnimatedSection from "./AnimatedSection";

const FounderTribute = () => {
    return (
        <section className="relative py-32 overflow-hidden">
            {/* <div className="absolute inset-0 bg-linear-to-b from-black via-gray-900/95 to-black" /> */}
            <div className="absolute -inset-8 bg-linear-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10 rounded-3xl blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            <div className="absolute inset-0 hidden md:block">
                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={i}
                        animate={{
                            y: [0, -120, 0],
                            x: [0, Math.sin(i) * 60, 0],
                            opacity: [0.1, 0.4, 0.1],
                        }}
                        transition={{
                            duration: 12 + i,
                            repeat: Infinity,
                            delay: i * 0.3,
                            ease: "easeInOut"
                        }}
                        className="absolute w-40 h-40 rounded-full blur-2xl bg-linear-to-r from-amber-500/5 via-yellow-500/5 to-amber-500/5"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                    />
                ))}
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4">
                <AnimatedSection delay={0.2}>
                    <div className="text-center mb-16">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "backOut" }}
                            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-linear-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 backdrop-blur-md border-2 border-amber-100/30 mb-8"
                        >
                            <motion.div
                                animate={{ rotate: [0, 0] }}
                                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                            >
                                <Trophy className="w-6 h-6 text-amber-200" />
                            </motion.div>
                            <span className="text-amber-200 font-bold text-lg tracking-wider uppercase">Fondateur & Visionnaire</span>
                        </motion.div>

                        <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-4">
                            Un <span className="bg-linear-to-r from-amber-100 via-yellow-100 to-amber-100 bg-clip-text text-transparent">Héritage</span> d'Excellence
                        </h2>

                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            Le visionnaire qui a fondé les bases de notre succès
                        </p>
                    </div>
                </AnimatedSection>

                <AnimatedSection delay={0.4}>

                    <motion.div whileHover={{ scale: 1.005 }} className="relative group">

                        <motion.div
                            animate={{
                                opacity: [0.3, 0.6, 0.3],
                                scale: [1, 1.1, 1],
                            }}
                            transition={{
                                duration: 5,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                            className="absolute -inset-12 bg-linear-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 rounded-3xl blur-3xl"
                        />

                        <div className="relative bg-linear-to-br from-gray-900/90 via-gray-800/90 to-gray-900/90 backdrop-blur-2xl rounded-3xl border-2 border-amber-500/20 shadow-2xl overflow-hidden">

                            <div className="absolute top-0 left-0 w-32 h-32 bg-linear-to-br from-amber-500/10 to-transparent rounded-br-full" />
                            <div className="absolute bottom-0 right-0 w-32 h-32 bg-linear-to-tl from-amber-500/10 to-transparent rounded-tl-full" />

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 p-6 md:p-12 lg:p-20">

                                {/* COLONNE GAUCHE : IMAGE & STATS CLÉS */}
                                <div className="relative flex flex-col items-center lg:justify-center">

                                    <div className="relative">
                                        <motion.div
                                            className="absolute -inset-8 md:-inset-16 bg-linear-to-r from-amber-100/20 via-yellow-100/20 to-amber-100/20 rounded-full blur-2xl md:blur-[80px]"
                                        />

                                        <div className="relative w-64 h-64 md:w-96 md:h-96 rounded-full overflow-hidden border-4 border-amber-500/30 shadow-2xl group/img">
                                            {/* <div className="absolute inset-0 bg-linear-to-br from-amber-500/30 to-transparent z-20 group-hover/img:opacity-0 transition-opacity duration-500" /> */}
                                            <img
                                                src="/Images_site_myriade/fondateur.jpeg"
                                                alt="Kodzo Mensah KONUTSE - Fondateur"
                                                className="w-full h-full object-cover relative z-10 transition-transform duration-700 group-hover/img:scale-110"
                                                style={{ objectPosition: 'center center' }}
                                            />
                                        </div>

                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                                            className="absolute -top-6 -right-6 w-20 h-20 md:w-32 md:h-32 border-4 border-amber-500/20 rounded-full"
                                        />

                                        <motion.div
                                            animate={{ rotate: -360 }}
                                            transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
                                            className="absolute -bottom-6 -left-6 w-24 h-24 md:w-40 md:h-40 border-4 border-yellow-500/20 rounded-full"
                                        />
                                    </div>

                                    <motion.div
                                        initial={{ scale: 0.9, opacity: 0 }}
                                        whileInView={{ scale: 1, opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.8 }}
                                        className="mt-12 px-10 py-6 rounded-3xl bg-linear-to-r from-amber-500/25 to-yellow-500/25 border-2 border-amber-500/40 shadow-xl backdrop-blur-md"
                                    >
                                        <div className="text-center">
                                            <div className="text-4xl font-black text-amber-400 mb-1 leading-none">30+</div>
                                            <div className="text-sm text-amber-100/80 font-bold uppercase tracking-widest">Années de Leadership</div>
                                        </div>
                                    </motion.div>

                                </div>

                                {/* COLONNE DROITE : CONTENU TEXTUEL & CITATION */}
                                <div className="space-y-10 flex flex-col justify-center items-center lg:items-start text-center lg:text-left">

                                    <div className="space-y-6 w-full">
                                        <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-linear-to-r from-amber-500/20 to-yellow-500/20 border-2 border-amber-500/40">
                                            <Trophy className="w-5 h-5 text-amber-400" />
                                            <span className="text-amber-300 font-bold text-sm tracking-widest uppercase">Fondateur</span>
                                        </div>

                                        <div className="space-y-2">
                                            <h3 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight">
                                                Kodzo Mensah <br />
                                                <span className="text-amber-400">KONUTSE</span>
                                            </h3>
                                            <p className="text-xl md:text-2xl text-amber-200/80 font-medium tracking-wide italic">
                                                Architecte de notre Vision
                                            </p>
                                        </div>

                                        <div className="space-y-6 max-w-2xl">
                                            <p className="text-lg md:text-xl text-gray-300 leading-relaxed font-light">
                                                Professionnel chevronné de la réassurance avec plus de <span className="text-amber-400 font-bold">trente (30) années d'expertise</span>. Sa carrière à la Direction du Système d'Informatique a façonné sa vision stratégique.
                                            </p>
                                            <p className="text-lg md:text-xl text-gray-300 leading-relaxed font-light">
                                                Fondateur de <span className="text-white font-bold tracking-wide">MYRIADE</span>, il transforme chaque défi technologique en une opportunité d'excellence et d'innovation durable.
                                            </p>
                                        </div>
                                    </div>

                                    {/* CITATION INTÉGRÉE */}
                                    <div className="relative w-full group/quote mt-4">
                                        <div className="absolute -inset-2 bg-linear-to-r from-amber-500/10 to-yellow-500/10 rounded-2xl blur-lg opacity-0 group-hover/quote:opacity-100 transition-opacity" />
                                        <div className="relative p-6 md:p-8 rounded-2xl bg-white/5 border border-amber-500/30 overflow-hidden backdrop-blur-sm">
                                            <div className="absolute top-0 right-0 p-4 font-serif text-8xl text-amber-500/10 leading-none select-none">"</div>
                                            <p className="text-lg md:text-xl text-gray-200 italic leading-relaxed relative z-10 pr-8">
                                                "L'excellence technologique ne se mesure pas seulement par l'innovation, mais par l'impact positif qu'elle crée pour ceux que nous servons."
                                            </p>
                                        </div>
                                    </div>

                                    {/* BADGES D'EXPERTISE */}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 w-full pt-4">
                                        {[
                                            { icon: CheckCircle, label: "30+ ans", sublabel: "Expérience" },
                                            { icon: Trophy, label: "Expert", sublabel: "Digital" },
                                            { icon: Star, label: "Leader", sublabel: "Visionnaire" }
                                        ].map((item, index) => (
                                            <div
                                                key={index}
                                                className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex flex-col items-center lg:items-start transition-all hover:bg-amber-500/20"
                                            >
                                                <item.icon className="w-6 h-6 text-amber-400 mb-2" />
                                                <div className="text-white font-bold text-sm uppercase tracking-wider">{item.label}</div>
                                                <div className="text-[10px] text-gray-400 uppercase">{item.sublabel}</div>
                                            </div>
                                        ))}
                                    </div>

                                </div>

                            </div>
                        </div>
                    </motion.div>
                </AnimatedSection>
            </div>
        </section>
    );
};

export default FounderTribute;
